// eslint-ignore-next-line

import CoreProTable, { CoreProTableState } from '../../core/components/CoreProTable';
import { DepositWallet } from '../../domain/usdt-providers/walletProvider';
import React, { useEffect, useRef, useState } from 'react';
import { ProCoreActionType } from '@ant-design/pro-utils';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { usePageState } from '../../core/hooks/usePageState';
import { Button, Input, message, Modal, PageHeader, Space, Tooltip, Typography } from 'antd';
import { transfer2Elapsed, transfer2LocalTime } from '../../core/utils/timeFormat';
import SystemIdSearchList from '../../domain/components/system-search-list/SystemIdSearchList';
import { UserWalletProvider } from '../../domain/usdt-providers/userWalletProvider';
import { AbandonWalletProvider } from '../../domain/usdt-providers/AbandonWalletProvider';
import { WRITE_ABANDON_WALLET } from '../../abandon-wallet/configs/scopes';
import {
  ChannelValueEnum,
  isRetireSupported,
  RETIRE_UNSUPPORTED_HINT,
  Token,
} from '../../domain/channel/channel';
import { useTranslation } from 'react-i18next';
import { useOperatorLogger } from '../../core/hooks/useOperatorLogger';
import { ADJUST_PAYMENT } from '../configs/scopes';
import _module from '../configs/module';
import ChangeAccountForm, { ChangeAccountProps, FormCloseEvent } from '../components/ChangeAccountForm';
import { EventProvider } from '../../../event/eventProvider';
import { filter } from 'rxjs/operators';
import * as profitForm from '../components/ProfitWithdrawFrom';
import { useUserWalletCollectStatusTranslate } from '../../domain/order-status/orderStatusTranslateHook';
import { OPERATOR_COLLOECT } from '../../orders/configs/scopes';
import { CollectTaskProvider } from '../../domain/usdt-providers/collectTaskProvider';
import { useSelector } from 'react-redux';
import { GlobalStoreMerge } from '../../../stores/merge';
import { UserStore } from '../../../stores/user';
import { normalizeOtp } from '../../core/utils/otp';

interface PageOp extends CoreProTableState<DepositWallet> {
}

const defaultChangeAccount: ChangeAccountProps = {
  availableBalance: 0,
  txnBalance: 0,
  channel: "",
  token: "",
  systemId: "",
  visible: false,
  walletAddress: "",
  walletType: "User"
};

// eslint-disable-next-line
export default (props: { title: string; subTitle: string }) => {
  const user = useSelector<GlobalStoreMerge, UserStore>((store) => store.user);
  const userWalletCollectStatusTranslateEnum = useUserWalletCollectStatusTranslate();
  const actionRef = useRef<ProCoreActionType>();
  const searchFormRef = useRef<FormInstance>();
  const { t } = useTranslation();
  const pageOp = usePageState<PageOp>("PAGE_REMEMBER", {
    params: {},
    collapsed: false,
    pagination: {
      pageSize: 10,
      current: 1
    },
    needRequest: true
  });
  const [changeAccount, setChangeAccount] = useState<ChangeAccountProps>(
    defaultChangeAccount
  );
  const loggger = useOperatorLogger();
  useEffect(() => {
    searchFormRef.current?.setFieldsValue(pageOp.get().params);
  }, [searchFormRef, pageOp]);
  
  useEffect(() => {
    EventProvider.subject
      .pipe(filter((x) => x.eventSymbol === FormCloseEvent.symbol))
      .subscribe(() => {
        setChangeAccount(defaultChangeAccount);
      });
    EventProvider.subject
      .pipe(
        filter(
          (x) =>
            x.eventSymbol === FormCloseEvent.symbol ||
            x.eventSymbol === profitForm.FormClosedEvent.symbol
        )
      )
      .subscribe(() => {
        refresh();
      });
    // eslint-disable-next-line
  }, []);
  
  const refresh = () => {
    pageOp.update((v) => {
      v.needRequest = true;
      return v;
    });
    actionRef.current?.reload();
  };
  // 手動歸集入口已停用(2026-08-12)—— 歸集一律走 poke-jobs 自動排程。
  // 端點(/admin/payment/collect/single)與這支呼叫邏輯都保留,只拿掉按鈕。
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const doCollect = (row: DepositWallet) => {
    Modal.confirm({
      title: t("collect.collect_description.title"),
      content: t("collect.collect_description.content"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      centered: true,
      onOk: () => {
        return CollectTaskProvider.adminCreateCollectTask({
          channel: row.channel,
          token: row.token,
          srcAddress: row.walletAddress,
          systemId: row.systemId,
          userId: user.userId
        })
          .then((res) => {
            const rs = res;
            Modal.success({
              title: t("collect.success"),
              content: (
                <>
                  <br />
                  <div key={1}>
                    <strong>{t("payment.wallet_address")}：</strong>
                    {rs.data.srcAddress}
                  </div>
                  <div key={2}>
                    <strong>{t("collect.task_id")}：</strong>
                    {rs.data.taskId}
                  </div>
                </>
              ),
              okText: t("common.confirm")
            });
          })
          .finally(() => {
            pageOp.update((v) => ({ ...v, needRequest: true }));
            actionRef.current?.reload();
          });
      }
    });
  };
  // 棄用單顆錢包:加入棄用清單後,poke-jobs 不再自動歸集/刷新它的餘額。
  // 沿用既有 abandon-wallets 端點(需 2FA + boSign)—— 棄用清單是掃款候選池,故保留 2FA。
  const doAbandon = (row: DepositWallet) => {
    let code = "";
    Modal.confirm({
      title: "棄用此錢包",
      width: 480,
      centered: true,
      content: (
        <div>
          <p>
            錢包 <strong>{row.walletAddress}</strong> 將加入棄用清單。
          </p>
          <p style={{ color: "#a8071a" }}>
            棄用後系統不再自動歸集、也不再刷新其餘額。若鏈上仍有殘值,需另走一次性回收。
          </p>
          <p style={{ marginBottom: 4 }}>請輸入 2FA 驗證碼:</p>
          <Input
            placeholder="2FA 驗證碼"
            onChange={(e) => (code = e.target.value)}
          />
        </div>
      ),
      okText: "確認棄用",
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        if (!code) {
          message.error("請輸入 2FA 驗證碼");
          throw new Error("no code");
        }
        try {
          const r = await AbandonWalletProvider.abandonWallets({
            addresses: [row.walletAddress],
            code: normalizeOtp(code),
          });
          if (r.notFound?.length) {
            message.warning(
              `這顆不在商戶/用戶錢包裡,未加入:${r.notFound.join(", ")}`,
              8
            );
          } else {
            message.success(`已棄用 ${r.inserted} 列`);
          }
          loggger.log({
            action: "棄用錢包",
            payload: { walletAddress: row.walletAddress },
            systemId: row.systemId,
          });
          refresh();
        } catch (e: any) {
          // 不再自己跳 message —— adminRequest 攔截器已經對每個失敗的請求跳過一次了,
          // 這裡再跳一次會變成兩個一模一樣的紅框,看起來像失敗了兩次。
          // 仍然往外丟,Modal 才會停在原地讓人重打驗證碼。
          throw e;
        }
      },
    });
  };

  // 停用單顆用戶錢包(available=0)—— 退役前的必經步驟,停用後才准棄用。需 2FA。
  const doDisable = (row: DepositWallet) => {
    let code = "";
    Modal.confirm({
      title: "停用此用戶錢包",
      width: 480,
      centered: true,
      content: (
        <div>
          <p>
            錢包 <strong>{row.walletAddress}</strong> 將被停用(available=0)。
          </p>
          <p style={{ color: "#a8071a" }}>
            停用後系統不再派它收新入金。恢復需走 SQL 或撤回棄用。
          </p>
          <p style={{ marginBottom: 4 }}>請輸入 2FA 驗證碼:</p>
          <Input
            placeholder="2FA 驗證碼"
            onChange={(e) => (code = e.target.value)}
          />
        </div>
      ),
      okText: "確認停用",
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: async () => {
        if (!code) {
          message.error("請輸入 2FA 驗證碼");
          throw new Error("no code");
        }
        try {
          const r = await AbandonWalletProvider.disableWallets({
            addresses: [row.walletAddress],
            code: normalizeOtp(code),
          });
          if (r.notFound?.length) {
            message.warning(`未找到:${r.notFound.join(", ")}`, 8);
          } else {
            message.success(`已停用 ${r.disabled} 列`);
          }
          refresh();
        } catch (e: any) {
          // 同上:攔截器已經跳過訊息了,這裡只負責讓 Modal 留著。
          throw e;
        }
      },
    });
  };
  return (
    <>
      <PageHeader title={props.title} subTitle={props.subTitle} extra={<></>} />
      <CoreProTable
        bordered
        rowKey="walletAddress"
        updateTableState={(state) =>
          pageOp.update((v: any) => ({ ...v, ...state }))
        }
        scroll={{ x: "fixed" }}
        tableState={pageOp.get()}
        
        requestData={(params) =>
          UserWalletProvider.getDepositWalletPagination(params).then((rs) => {
            loggger.log({
              action: "查詢",
              payload: { req: params },
              systemId: params.systemId
            });
            return rs;
          })
        }
        
        actionRef={actionRef as any}
        formRef={searchFormRef}
        columns={[
          {
            fixed: "left",
            hideInSearch: false,
            width: 80,
            align: "center",
            title: "商戶名稱",
            dataIndex: "systemId",
            renderFormItem: (_: any, row, form: FormInstance<any>) => (
              <SystemIdSearchList
                onChange={(value) =>
                  form.setFieldsValue({ [_.dataIndex as string]: value })
                }
                systemId={pageOp.get().params.systemId}
              />
            )
          },
          {
            hideInSearch: false,
            width: 100,
            ellipsis: true,
            align: "center",
            title: "使用者代號",
            dataIndex: "userId"
          },
          {
            align: "center",
            title: t("payment.channel"),
            dataIndex: "channel",
            valueType: "select",
            valueEnum: ChannelValueEnum,
            onFilter: true,
            width: 70
          },
          {
            align: "center",
            title: t("payment.token"),
            dataIndex: "token",
            valueType: "select",
            valueEnum: Token,
            onFilter: true,
            width: 70
          },
          {
            // TRON 地址 34 字,原本 width 100 + ellipsis 一定被截成「TXxx...」,
            // 要複製、要肉眼比對都做不到。改成整串顯示 + 一鍵複製。
            hideInSearch: false,
            width: 300,
            align: "center",
            title: "錢包地址",
            dataIndex: "walletAddress",
            render: (n, row) => (
              <Typography.Text
                copyable={{ text: row.walletAddress }}
                style={{ fontSize: 12, whiteSpace: "nowrap" }}
              >
                {row.walletAddress}
              </Typography.Text>
            )
          },
          {
            hideInSearch: true,
            width: 80,
            align: "right",
            title: "餘額",
            dataIndex: "balance"
          },
          {
            hideInSearch: true,
            width: 100,
            align: "center",
            title: "更新時間",
            dataIndex: "balanceUpdatedAt",
            render: (text: any, row: any) =>
              transfer2LocalTime(new Date(row.balanceUpdatedAt))
          },
          {
            // 開立時間 + 開了多久。第二行的「已開立 N 天」才是真正要看的 ——
            // 判斷這顆是新配的還是躺很久的老錢包,絕對時間反而要換算。
            hideInSearch: true,
            width: 110,
            align: "center",
            title: "開立時間",
            dataIndex: "createdAt",
            render: (text: any, row: any) =>
              !row.createdAt ? (
                <span style={{ color: "#999" }}>—</span>
              ) : (
                <>
                  <div>{transfer2LocalTime(new Date(row.createdAt))}</div>
                  <div style={{ color: "#999", fontSize: 12 }}>
                    已開立 {transfer2Elapsed(row.createdAt)}
                  </div>
                </>
              )
          },
          {
            hideInSearch: true,
            width: 80,
            align: "right",
            title: "帳變餘額",
            dataIndex: "txnBalance"
          },
          {
            hideInSearch: true,
            width: 80,
            align: "right",
            title: "可用餘額",
            dataIndex: "availableBalance"
          },
          {
            hideInSearch: true,
            hideInTable: false,
            width: 100,
            align: "center",
            title: t("collect.user_wallet_collect_status"),
            dataIndex: "collectStatus",
            valueEnum: userWalletCollectStatusTranslateEnum
          },
          {
            align: "center",
            width: 80,
            title: "可用狀態",
            dataIndex: "available",
            valueType: "select",
            valueEnum: {
              true: { text: "可用", status: "Success" },
              false: { text: "不可用", status: "Default" }
            }
          },
          {
            title: "操作",
            key: "actions",
            valueType: "option",
            fixed: "right",
            align: "center",
            render: (dom, row) => (
              <Space>
                <Button
                  key={2}
                  type="primary"
                  size={"small"}
                  disabled={parseFloat(row.txnBalance as any) === 0 || !_module.can([ADJUST_PAYMENT])}
                  onClick={(x) =>
                    setChangeAccount({
                      systemId: row.systemId,
                      visible: true,
                      walletAddress: row.walletAddress,
                      channel: row.channel,
                      token: row.token,
                      availableBalance: parseFloat(
                        row.availableBalance
                      ),
                      txnBalance: parseFloat(row.txnBalance),
                      walletType: "User"
                    })
                  }
                >
                  調帳
                </Button>
                {/* 手動歸集鈕已停用(2026-08-12)—— 歸集改由 poke-jobs 自動排程負責。
                {row.collectStatus === "UnCollect" && (<Button
                  key={3}
                  size={"small"}
                  disabled={!_module.can([OPERATOR_COLLOECT])}
                  type="primary"
                  onClick={() => doCollect(row)}
                >
                  {t("collect.collect")}
                </Button>)}
                */}
                {row.available &&
                  _module.can([WRITE_ABANDON_WALLET], "ABANDON-WALLET") && (
                    <Tooltip
                      key={5}
                      title={
                        isRetireSupported(row.channel)
                          ? ""
                          : RETIRE_UNSUPPORTED_HINT
                      }
                    >
                      <Button
                        size={"small"}
                        danger
                        disabled={!isRetireSupported(row.channel)}
                        onClick={() => doDisable(row)}
                      >
                        停用
                      </Button>
                    </Tooltip>
                  )}
                {!row.available &&
                  _module.can([WRITE_ABANDON_WALLET], "ABANDON-WALLET") && (
                    <Tooltip
                      key={4}
                      title={
                        isRetireSupported(row.channel)
                          ? ""
                          : RETIRE_UNSUPPORTED_HINT
                      }
                    >
                      <Button
                        size={"small"}
                        danger
                        disabled={!isRetireSupported(row.channel)}
                        onClick={() => doAbandon(row)}
                      >
                        棄用
                      </Button>
                    </Tooltip>
                  )}
              </Space>
            )
          }
        ]}
      />
      <ChangeAccountForm {...changeAccount} />
    </>
  );
};
